import { formatMoneyObject } from 'shared/lib/formatMoneyObject'

/**
 * Converts a UTC date string to Eastern Time (ET).
 * ET is UTC-5 (EST) or UTC-4 (EDT) depending on DST.
 */
const toEasternTime = (dateStr: string): Date => {
  const utcDate = new Date(dateStr)
  // Convert to ET using Intl.DateTimeFormat which handles DST automatically
  const etString = utcDate.toLocaleString('en-US', {
    timeZone: 'America/New_York',
  })
  return new Date(etString)
}

export const formatRaffleDate = (dateStr: string) => {
  const date = toEasternTime(dateStr)
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const yy = String(date.getFullYear()).slice(-2)
  return `${mm}.${dd}.${yy}`
}

export const formatRaffleDateTime = (dateStr: string) => {
  const date = toEasternTime(dateStr)
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const yy = String(date.getFullYear()).slice(-2)
  const hours = date.getHours()
  const isPM = hours >= 12
  const displayHours = hours % 12 || 12
  const ampm = isPM ? 'pm' : 'am'
  return `${mm}.${dd}.${yy} at ${displayHours}${ampm} ET`
}

export const formatStoreList = (stores: string[]): string => {
  if (stores.length === 0) return ''
  if (stores.length === 1) return stores[0]
  return `${stores.slice(0, -1).join(', ')} and ${stores[stores.length - 1]}`
}

export const formatRafflePriceForMeta = (price: number, currency: string): string =>
  formatMoneyObject(
    { amount: price * 100, amountUsdCents: price * 100, currency },
    { showCents: false, useGrouping: false },
  )
export const formatRaffleDateLong = (dateStr: string): string => {
  const date = toEasternTime(dateStr)
  const month = date.toLocaleString('en-US', { month: 'long' })
  const day = date.getDate()
  const year = date.getFullYear()
  const hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const isPM = hours >= 12
  const displayHours = hours % 12 || 12
  const ampm = isPM ? 'PM' : 'AM'
  return `${month} ${day}, ${year} at ${displayHours}:${minutes} ${ampm} ET`
}

/**
 * Formats size unit and gender for display without any casing transforms.
 * Returns base format like "US Men's" or "JP Women's" or just "US"
 * Use CSS text-transform for uppercase as needed in the UI
 *
 * Examples:
 * - formatSizeLabel('US', 'men') => "US Men's"
 * - formatSizeLabel('JP', 'women') => "JP Women's"
 * - formatSizeLabel('US') => "US"
 */
export const formatSizeLabel = (sizeUnit?: string, gender?: string): string => {
  const unit = sizeUnit?.toUpperCase() || 'US'

  if (!gender) return unit

  const lowerGender = gender.toLowerCase()
  if (lowerGender === 'men') {
    return `${unit} Men's`
  }
  if (lowerGender === 'women') {
    return `${unit} Women's`
  }

  // Fallback for any other gender value
  const formatted = gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()
  return `${unit} ${formatted}`
}
